import SyntaxError from '.SyntaxError';
import Map from '.Map';
import freeze from '.Object.freeze';
import preventExtensions from '.Object.preventExtensions';
import defineProperties from '.Object.defineProperties';
import getPrototypeOf from '.Object.getPrototypeOf';
import apply from '.Reflect.apply';
import push from '.Array.prototype.push';
import test from '.RegExp.prototype.test';
import fromCodePoint from '.String.fromCodePoint';
import parseInt from '.parseInt';
import undefined from '.undefined';
import getOwnPropertyDescriptors from '.null.getOwnPropertyDescriptors';
import throwSyntaxError from '.throw.SyntaxError';
import toStringTag from '.Symbol.toStringTag';

import { newRegExp } from '@ltd/j-regexp';

import * as is from './is';

export const protect = (constructor                                            , pop          )       => /*#__PURE__*/( ()       => {
	const { prototype } = constructor;
	pop && defineProperties(prototype, { ...getOwnPropertyDescriptors(getPrototypeOf(prototype)), ...getOwnPropertyDescriptors(prototype) });
	freeze(prototype);
	constructor.default = undefined;
	preventExtensions(constructor);
} )();

export const notKeyframes = /*#__PURE__*/test.bind(/^(?:(?:default|in(?:herit|itial)|none|revert|unset)?$|['"])|,/i);

/// -- var()
/// @counter-style list-style -type
/// @font-face font -family

export const nonASCII = /\x80-\uFFFF/i;
const hex_digit = /[0-9A-F]/i;
const escape = /*#__PURE__*/( () => newRegExp.i `
	\\
	(?:
		${hex_digit}{1,6}
		(?:[\t\n\f ]|\r\n?)?
		|
		[^\n\f\r]
	)
` )();
const ws = /\t\n\f\r /i;
export const ident_token_start = /*#__PURE__*/( () => newRegExp.i `
	(?:
		-
		(?:
			-
		|
			[a-z_${nonASCII}]
		|
			${escape}
		)
	|
		[a-z_${nonASCII}]
	|
		${escape}
	)
` )();
export const ident_token = /*#__PURE__*/( () => newRegExp.i `
	${ident_token_start}
	(?:
		[-\w${nonASCII}]
	|
		${escape}
	)*
` )();
export const isCompoundSelector = /*#__PURE__*/( () => newRegExp.i `
	^(?!$)
	(?:${ident_token}|\*)?
	(?:\|(?:${ident_token}|\*))?
	(?:\.${ident_token})*
	$
`.test )();
export const SIMPLE_SELECTORS = /*#__PURE__*/( () => newRegExp.gi `
	(?:[.|]?${ident_token}|\*)
` )();
const hash_token = /*#__PURE__*/( () => newRegExp.i `
	#
	(?:
		[-\w${nonASCII}]
	|
		${escape}
	)*
` )();
export const isIdSelector = /*#__PURE__*/( () => newRegExp.i `
	^${hash_token}$
`.test )();
const string_token = /*#__PURE__*/( () => newRegExp.is `
	"
	(?:\\(?:\r\n|.)|[^\\"\n\f\r])*
	"?
|
	'
	(?:\\(?:\r\n|.)|[^\\'\n\f\r])*
	'?
` )();
const url_token = /*#__PURE__*/( () => newRegExp.is `
	url
	(?:
		\(
		[${ws}]*
		(?![${ws}"'])
		(?:
			${escape}
		|
			[^)]
		)*
		\)?
	|
		-prefix\([${ws}]*\)
	)
|
	domain\([${ws}]*	[a-z\d\-.:]*	[${ws}]*\)
` )();
const ESCAPE = /\\(?:([\dA-F]{1,6})(?:[ \t\n\f]|\r\n?)?|([\n\f]|\r\n?)|(.))/gis;
export const evaluate = (literal        ) => literal.replace(ESCAPE, (match        , hex         , char         ) => hex ? fromCodePoint(parseInt(hex, 16)) : char ?? '');
export const _ws_ = /*#__PURE__*/( () => newRegExp.gi `
	^
		${ws}+
	|
		${ws}+
	$
` )();
const number_token = /*#__PURE__*/( () => newRegExp.i `
	[-+]?
	(?:\d+(?:\.\d+)?|\.\d+)
	(?:e[+-]?\d+)?
` )();
const CDO_token = '<!--';
const CDC_token = '-->';

const TOKENS = /*#__PURE__*/( () => newRegExp.gis `
	(?:
		[${ws}]+
	|
		/\*.*?\*/
	)+
|
	/\*.*
|
	${url_token}
	|
	${ident_token}\(?
	|
	@${ident_token}
|
	${number_token.source}(?:${ident_token}|%)?
|
	${hash_token}
|
	${string_token}
|
	${CDO_token}
|
	${CDC_token}
|
	.
` )();

const IS_BAD_URL = /*#__PURE__*/( () => newRegExp.i `
	^
	url\(
	(?!
			[${ws}]*
			(?:${escape}|[^${ws}"'()\\])*
			[${ws}]*
		\)
		$
	)
`.test )();
const IS_NUMBER = /*#__PURE__*/test.bind(/[\d.]/);
const COMMENT = /\/\*.*?\*\//gs;
const IS_IDENT = /*#__PURE__*/( () => newRegExp.i `
	^
	${ident_token}
	$
`.test )();
const IS_FUNCTION = /*#__PURE__*/( () => newRegExp.i `
	^
	${ident_token}\(
	$
`.test )();

export const comment = 'c';
export const whitespace = 'w';
export const ident = 'i';
export const atKeyword = 'a';
export const function_ = 'f';
export const hash = 'h';
export const string = 's';
export const number = 'n';
export const dimension = 'd';
export const percentage = 'p';
export const url = 'u';

const IdentLike = (literal        ) =>
	IS_IDENT(literal) ? ident :
		IS_FUNCTION(literal) ?
			is.url_prefix_ (literal) ? throwSyntaxError(`function-token "url-prefix" 不在标准中，而它此刻的内容又存在歧义`) :
				is.domain_ (literal) ? throwSyntaxError(`function-token "domain" 不在标准中，而它此刻的内容又存在歧义`) :
					function_ :
			IS_BAD_URL(literal) ? throwSyntaxError(`bad-url-token`) :
				url;

const Numeric = (literal        ) => {
	const rest = literal.replace(number_token, '');
	return rest ? rest==='%' ? percentage : dimension : number;
};

                                                                                           
	                                                                                     
	                                                                                    
const Type = (literal        )       => {
	switch ( literal[0] ) {
		case '\t':
		case '\n':
		case '\f':
		case '\r':
		case ' ':
			return whitespace;
		case '!':
			return literal       ;
		case '"':
			return literal[literal.length - 1]==='"' && literal.length!==1 ? literal.includes('\t') ? throwSyntaxError(`CSS 2 不允许字符串中存在 TAB`) : string : throwSyntaxError(`bad-string-token`);
		case '#':
			return hash;
		case '$':
		case '%':
		case '&':
			return literal                   ;
		case '\'':
			return literal[literal.length - 1]==='\'' && literal.length!==1 ? literal.includes('\t') ? throwSyntaxError(`CSS 2 不允许字符串中存在 TAB`) : string : throwSyntaxError(`bad-string-token`);
		case '(':
		case ')':
		case '*':
			return literal                   ;
		case '+':
			return literal==='+' ? literal : Numeric(literal);
		case ',':
			return literal       ;
		case '-':
			if ( literal==='-' ) { return literal; }
			if ( IS_NUMBER(literal[1] ) ) { return Numeric(literal); }
			if ( literal==='-->' ) { return throwSyntaxError(`CDC-token`); }
			break;
		case '.':
			return literal==='.' ? literal : Numeric(literal);
		case '/':
			if ( literal==='/' ) { return literal; }
			literal = literal.replace(COMMENT, '');
			return literal
				? literal[0]==='/'
					? throwSyntaxError(`bad-comment-token`)
					: whitespace
				: comment;
		case '0':
		case '1':
		case '2':
		case '3':
		case '4':
		case '5':
		case '6':
		case '7':
		case '8':
		case '9':
			return Numeric(literal);
		case ':':
		case ';':
			return literal             ;
		case '<':
			return literal==='<' ? literal : throwSyntaxError(`CDO-token`);
		case '=':
		case '>':
		case '?':
			return literal                   ;
		case '@':
			return literal==='@' ? literal : atKeyword;
		case '[':
			return literal       ;
		case '\\':
			if ( literal==='\\' ) { throw SyntaxError(`bad escape`); }
			break;
		case ']':
		case '^':
		case '`':
		case '{':
		case '|':
		case '}':
		case '~':
			return literal                                           ;
	}
	return IdentLike(literal);
};

const LITERALS                    = [];
export let literals                    = LITERALS;
export let literal         = '';
export let types                           = ''       ;
export let type       = ''       ;
export let length         = 0;
export let index         = 0;
export let currentLayer                    = null;
export const collection_type                 = [];
export const collection_class                  = [];
export const collection_attribute                      = [];
export const collection_id               = [];
export const collection_url        = [];
export const collection_keyframes                  = [];
export const collection_animation         = [];
export let collection_selector                 = [];

export class KindMap                                                           extends Map                               {
	static default = protect(this, true);
	constructor () { return super()                   ; }
	fetch                                                           (                     layers           , key                   , addon         ) {
		const { length } = layers;
		let index = 0;
		while ( index!==length ) {
			const layer = layers[index++]     ;
			const literal = layer[key           ]          ;
			let array                                         = this.get(literal)                                    ;
			array ?? this.set(literal, array = []                        );
			addon && ( array = array[addon] ?? ( array[addon] = [] ) );
			array[array.length] = layer;
		}
		return this;
	}
	merge                                         (                  layers     , literal        ) {
		const array = this.get(literal);
		array
			? apply(push, array, layers)
			: this.set(literal, layers);
	}
}

export class Refs {
	type = collection_type.length
		? new KindMap              ().fetch(collection_type, 'literal')
		: null;
	class = collection_class.length
		? new KindMap               ().fetch(collection_class, 'itemLiteral')
		: null;
	attribute = collection_attribute.length
		? new KindMap                   ().fetch(collection_attribute, 'nameLiteral')
		: null;
	id = collection_id.length
		? new KindMap            ().fetch(collection_id, 'valueLiteral')
		: null;
	url = collection_url.length
		? new KindMap     ().fetch(collection_url, 'valueLiteral')
		: null;
	animationKeyframes                                      = collection_keyframes.length || collection_animation.length
		? new KindMap                     ().fetch(collection_keyframes, '_nameLiteral', 'rule').fetch(collection_animation, '_literal')
		: null;
	selector                        = collection_selector.length
		? collection_selector
		: null;
	constructor () {
		if ( collection_selector.length ) { collection_selector = []; }
		return this;
	}
}

const IS_NULL_SURROGATE = /*#__PURE__*/test.bind(/[\x00\uD800-\uDFFF]/u);
const IS_NON_PRINTABLE = /*#__PURE__*/test.bind(/[\x00-\x08\x0B\x0E-\x1F\x7F]/);
const NOT_CHANGES = /\\.?|[^\\\x80-\x9F]+/gs;
export const check = (css        ) => {
	if ( css[0]==='\uFEFF' ) { throw SyntaxError(`CSS 中 UTF BOM（U+FEFF）算作普通字符，处于起始位置时很可能是误用`); }
	if ( IS_NULL_SURROGATE(css) ) { throw SyntaxError(`CSS 中 NUL（U+00）或残破的代理对码点（U+D800〜U+DFFF）字面量会被替换为 U+FFFD，请避免使用`); }
	if ( IS_NON_PRINTABLE(css) ) { throw SyntaxError(`CSS 中不能出现除 TAB、LF、FF、CR 以外的控制字符字面量`); }
	if ( css.replace(NOT_CHANGES, '') ) { throw SyntaxError(`U+80～U+9F 字面量在 CSS 2 和 3 之间表现不同，请避免使用`); }
};
export const tokenize = (css        )                                           => {/// type / literal / subParts
	if ( !css ) { return { literals: [], errors: [] }; }
	check(css);
	const literals = css.match(TOKENS) ;
	const errors           = [];
	let index = 0;
	for ( const { length } = literals; index!==length; ++index ) {
		try { Type(literals[index] ); }
		catch { errors[errors.length] = index; }
	}
	return { literals, errors };
};

export const parent = Symbol('parent');
export const consume = Symbol('consume');
export const stay = () => { --index; };
export const parse = (sheet       , source        ) => {
	literals = { length } = source.match(TOKENS) ;
	index = 0;
	//@ts-ignore
	while ( index!==length ) { types += Type(literals[index++]); }
	currentLayer = sheet;
	index = 0;
	while ( index!==length ) {
		type = types[index] ;
		literal = literals[index++] ;
		currentLayer = currentLayer[consume]() ?? throwSyntaxError(`CSS 中出现了 ${currentLayer[toStringTag       ]} 上下文不允许的内容“${literal}”：\n${( literals             ).slice(0, index - 1).join('')}`);
	}
	currentLayer===sheet || currentLayer[parent]===sheet && !( currentLayer                  ) .block || throwSyntaxError(`CSS 终止处尚有未完成的结构`);
	return (
		collection_type.length ||
		collection_class.length ||
		collection_attribute.length ||
		collection_id.length ||
		collection_url.length ||
		collection_keyframes.length ||
		collection_animation.length ||
		collection_selector.length
			? new Refs : null );
};

export const clear = ()       => {
	collection_type.length =
	collection_class.length =
	collection_attribute.length =
	collection_id.length =
	collection_url.length =
	collection_keyframes.length =
	collection_animation.length =
	collection_selector.length =
		0;
	literals = LITERALS;
	currentLayer = null;
	literal = types = ''       ;
};

                                 
                                 
                                               
                                                 
                                                         
                                           
                             
                                              
                                          
                                               